"use client";

import { useCallback, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

import Step1EmailForm from "@/components/form/step1-email-form";
import Step2AuthCodeForm from "@/components/form/step2-auth-code-form";
import Step3NewPasswordForm from "@/components/form/step3-new-password-form";

import { handleMutationError } from "@/lib/mutationErrorHandler";

import { resetConfirm } from "@/api/auth/resetConfirm";
import { resetPassword } from "@/api/auth/resetPassword";
import { resetRequest } from "@/api/auth/resetRequest";

import { useAlertStore } from "@/stores/alert-store";

import { toast } from "../app/app-toast";

export function ResetPasswordForm() {
  const [step, setStep] = useState(1);
  const router = useRouter();
  const openAlert = useAlertStore(state => state.openDialog);

  const [email, setEmail] = useState<string>("");
  const [emailCode, setEmailCode] = useState<string>("");
  const [authCodeError, setAuthCodeError] = useState<string>("");

  const { mutate: resetRequestMutate } = useMutation({
    mutationFn: (emailValue: string) => resetRequest(emailValue),
    onSuccess: data => {
      if (data.ok) {
        setStep(2);
        toast({
          type: "success",
          title: "성공",
          description: data.message || "인증코드 발송 성공했습니다.",
        });
      }
    },
    onError: (error: Error) => {
      handleMutationError(error, "발송 실패", "인증코드 발송에 실패했습니다.");
    },
  });

  const { mutate: resetConfirmMutate } = useMutation({
    mutationFn: ({ emailValue, codeValue }: { emailValue: string; codeValue: string }) =>
      resetConfirm(emailValue, codeValue),
    onSuccess: data => {
      if (data.ok) {
        setAuthCodeError("");
        setStep(3);
        toast({
          type: "success",
          title: "성공",
          description: data.message || "인증코드를 확인했습니다.",
        });
      }
    },
    onError: () => {
      setAuthCodeError("인증코드가 일치하지 않습니다");
    },
  });

  const { mutate: resetPasswordMutate } = useMutation({
    mutationFn: ({
      emailValue,
      codeValue,
      passwordValue,
    }: {
      emailValue: string;
      codeValue: string;
      passwordValue: string;
    }) => resetPassword(emailValue, codeValue, passwordValue),
    onSuccess: data => {
      if (data.ok) {
        openAlert({
          type: "success",
          title: "비밀번호 변경 완료",
          description: "비밀번호가 정상적으로 변경 완료되었습니다.",
          onAction: () => router.push("/sign-in"),
        });
      }
    },
    onError: (error: Error) => {
      handleMutationError(error, "변경 실패", "비밀번호 변경에 실패했습니다.");
    },
  });

  const handleStep1Next = useCallback(
    (emailValue: string) => {
      setEmail(emailValue);
      resetRequestMutate(emailValue);
    },
    [resetRequestMutate]
  );

  const handleStep2Next = useCallback(
    (codeValue: string) => {
      setAuthCodeError("");
      setEmailCode(codeValue);
      resetConfirmMutate({ emailValue: email, codeValue });
    },
    [email, resetConfirmMutate]
  );

  const handleStep3Next = useCallback(
    (passwordValue: string) => {
      resetPasswordMutate({ emailValue: email, codeValue: emailCode, passwordValue });
    },
    [email, emailCode, resetPasswordMutate]
  );

  const handleResendCode = useCallback(() => {
    setAuthCodeError("");
    if (email) {
      resetRequestMutate(email);
    }
  }, [email, resetRequestMutate]);

  return (
    <div className="mx-auto w-full max-w-md">
      {step === 1 && <Step1EmailForm onNext={handleStep1Next} />}
      {step === 2 && (
        <Step2AuthCodeForm
          onNext={handleStep2Next}
          onResendCode={handleResendCode}
          errorMessage={authCodeError}
        />
      )}
      {step === 3 && <Step3NewPasswordForm onNext={handleStep3Next} />}
    </div>
  );
}
